import {useCallback, useEffect, useState} from "react";
import {hasActiveTextSelection} from "./surveyBlackoutOffsets";

interface Options {
    /** Called on the second click of an armed bar. The caller owns what "restore" means. */
    onRestore: (barId: string) => void
    /** While true, bars are inert — a change is already in flight. */
    disabled?: boolean
}

/**
 * Restore is the confirm step in reverse: one click arms a bar, the next restores it, and a
 * mousedown anywhere else calls it off. No bare gesture destroys a bar on its own, which is what
 * makes a stray double-click harmless in both directions.
 */
export const useSurveyBlackoutArming = ({onRestore, disabled}: Options) => {
    const [armedId, setArmedId] = useState<string | null>(null);

    const disarm = useCallback(() => setArmedId(null), []);

    const handleBarClick = (barId: string) => {
        if (disabled) return;

        // A selection released on top of a bar ends in a click on that bar, with the selection still
        // live. That gesture belongs to the confirm bubble, not to arming.
        if (hasActiveTextSelection()) return;

        if (armedId !== barId) {
            setArmedId(barId);
            return;
        }

        setArmedId(null);
        onRestore(barId);
    };

    // Anything pressed that is not the armed bar disarms it — another bar, the text around it, the
    // pane, the page. Mousedown rather than click, so starting a new selection cancels the pending
    // restore before the drag even finishes.
    useEffect(() => {
        if (!armedId) return;

        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest?.(`[data-bar-id="${armedId}"]`)) return;
            setArmedId(null);
        };

        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [armedId]);

    return {armedId, handleBarClick, disarm};
};
